import clsx from 'clsx';
import {
  Children,
  forwardRef,
  isValidElement,
  type PropsWithChildren,
  type ReactElement,
  type ReactNode,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import { useIntl } from 'react-intl';

import { useFocusRing } from '../../core/hooks/useFocusRing.js';
import { useSplitProps } from '../../core/hooks/useSplitProps.js';
import { isSlot } from '../../core/utils/is-slot.js';
import { mergeProps as _mergeProps } from '../../core/utils/merge-props.js';
import { Divider } from '../../layouts/divider/Divider.js';
import { Flex } from '../../layouts/flex/Flex.js';
import { inputGroupClassName } from '../../layouts/input-group/InputGroup.sty.js';
import { InputGroup } from '../../layouts/input-group/InputGroup.js';
import { InternalBaseItem as BaseItem } from '../forms-core/components/form-field-messages/FormFieldMessageBaseItem.js';
import { useFormControl } from '../forms-core/hooks/useFormControl.js';
import {
  getI18nValidationState,
  useFormFieldValidation,
} from '../forms-core/hooks/useFormFieldValidation.js';
import { FormFieldMessagesAction } from '../forms-core/state/formfield-messages-state-reducer.js';
import { useAutofillEvent } from '../hooks/useAutofillEvent.js';
import { useContainerFocus } from '../hooks/useContainerFocus.js';
import type { FormControlRef } from '../shared-types.js';
import { useCustomMessageEmitter } from '../validation/useCustomMessageEmitter.js';
import { useValidationStateEmitter } from '../validation/useValidationStateEmitter.js';
import type { BaseInputProps } from './base-input.types.js';
import * as styles from './BaseInput.sty.js';
import { BaseInputButton } from './BaseInputButton.js';
import {
  BaseInputPrefix,
  BaseInputReservedSuffix,
  BaseInputSuffix,
} from './BaseInputContent.js';
import { getPlaceholderMessage } from './baseInputMessages.js';
import { ClearButton } from './ClearButton.js';
import { BaseInputContext } from './contexts/BaseInputContext.js';

type SlotContent = {
  buttons: ReactElement[];
  rest: ReactNode[];
  props: Record<string, unknown>;
};

function getSlots(children: ReactNode) {
  const validSlots = Children.toArray(children).filter(
    isValidElement<PropsWithChildren>,
  );

  let prefix: SlotContent = { buttons: [], rest: [], props: {} };
  let suffix: SlotContent = { buttons: [], rest: [], props: {} };
  let reservedSuffix: SlotContent = { buttons: [], rest: [], props: {} };

  for (const slot of validSlots) {
    const buttons: SlotContent['buttons'] = [];
    const rest: SlotContent['rest'] = [];

    const slotChildren = Children.toArray(slot.props.children);

    for (const child of slotChildren) {
      if (isSlot(child, BaseInputButton)) {
        buttons.push(child);
      } else {
        rest.push(child);
      }
    }

    const { children: _children, ...slotProps } = slot.props;

    if (slot.type === BaseInputPrefix) {
      prefix = { buttons, rest, props: slotProps };
    }
    if (slot.type === BaseInputSuffix) {
      suffix = { buttons, rest, props: slotProps };
    }
    if (slot.type === BaseInputReservedSuffix) {
      reservedSuffix = { buttons, rest, props: slotProps };
    }
  }

  return { prefix, suffix, reservedSuffix };
}

const BaseInputRoot = /* @__PURE__ */ forwardRef<
  FormControlRef<HTMLDivElement>,
  BaseInputProps
>((props, forwardedRef) => {
  const intl = useIntl();

  const defaultPlaceholder = intl.formatMessage(
    getPlaceholderMessage(props.type),
  );

  const {
    children,
    id: propId,
    disabled: propDisabled,
    required: propRequired,
    'aria-disabled': ariaDisabledProp,
    variant = 'default',
    // oxlint-disable-next-line typescript/no-deprecated -- APPDEV-18321
    controlState,
    readOnly: propReadOnly,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    'aria-label': ariaLabelProp,
    'aria-labelledby': ariaLabelledByProp,
    'aria-describedby': ariaDescribedByProp,
    'aria-details': ariaDetailsProp,
    className: consumerClassName,
    style: consumerStyle,
    // oxlint-disable-next-line typescript/no-deprecated
    onClear,
    onValidityChange,
    type,
    placeholder = defaultPlaceholder,
    value,
    pattern,
    max,
    min,
    step,
    maxLength,
    minLength,
    ...remainingProps
  } = props;

  // assures that always an empty string is set
  const inputValue = value ?? '';

  const {
    id,
    disabled,
    required,
    readOnly,
    ariaDisabled,
    inputProps,
    hintProps,
    hint,
    showError,
    messageDispatch,
  } = useFormControl({
    propId,
    propDisabled,
    propRequired,
    propAriaDisabled: ariaDisabledProp,
    propReadOnly,
    controlState,
  });

  const disabledOrAriaDisabled = disabled || ariaDisabled || false;

  const { isAutoFilled, onAnimationStart } = useAutofillEvent();

  const inputRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const _emitValidationState = useValidationStateEmitter(
    controlState ? undefined : onValidityChange,
  );
  const emitValidationState = useCustomMessageEmitter(
    _emitValidationState,
    () => getI18nValidationState(intl, inputRef.current, 'manual'),
  );

  const { focusProps, focusClassName } = useFocusRing({
    color: showError && !(disabled || ariaDisabled) ? 'critical' : 'neutral',
    ignoreModality: true,
    disabled,
  });

  const { onChange, onInvalid, ariaProps } = useFormFieldValidation(
    inputRef,
    {
      required,
      pattern,
      max,
      min,
      step,
      maxLength,
      minLength,
      type,
      value: inputValue as string,
    },
    {
      'aria-label': ariaLabelProp,
      'aria-labelledby': ariaLabelledByProp,
      'aria-describedby': ariaDescribedByProp,
      'aria-details': ariaDetailsProp,
    },
    undefined,
    _emitValidationState,
    controlState,
  );

  useImperativeHandle(
    forwardedRef,
    (): FormControlRef<HTMLDivElement> => ({
      element: wrapperRef.current,
      inputRef: inputRef.current,
      focus() {
        inputRef.current?.focus();
      },
      validate() {
        inputRef.current?.checkValidity();
        messageDispatch?.({
          type: FormFieldMessagesAction.SETISTOUCHED,
          payload: { isTouched: true },
        });
        emitValidationState(
          getI18nValidationState(intl, inputRef.current, 'manual'),
        );
      },
      // DEPRECATION: [APPDEV-18178] BREAKING-CHANGE: Remove validateAsync() and change validate() to return Promise<ValidationState>.
      validateAsync: async () => {
        inputRef.current?.checkValidity();
        messageDispatch?.({
          type: FormFieldMessagesAction.SETISTOUCHED,
          payload: { isTouched: true },
        });
        return emitValidationState(
          getI18nValidationState(intl, inputRef.current, 'manual'),
        );
      },
    }),
    [messageDispatch, emitValidationState, intl],
  );

  const context = useMemo(
    () => ({ variant, hasError: showError, disabled, ariaDisabled }),
    [variant, showError, disabled, ariaDisabled],
  );
  const slots = useMemo(() => getSlots(children), [children]);
  const hasInnerPrefix = slots.prefix.rest.length > 0;
  const hasInnerSuffix = slots.suffix.rest.length > 0;
  const hasInnerReservedSuffix = slots.reservedSuffix.rest.length > 0;
  const hasOnClear = onClear !== undefined;
  const showClearButton =
    hasOnClear && inputValue !== '' && !hasInnerReservedSuffix;

  const {
    matchedProps: dataAttributesProps,
    remainingProps: remainingInputProps,
  } = useSplitProps(remainingProps, [(key: string) => key.startsWith('data-')]);

  const { containerClassName, ...containerFocusProps } =
    useContainerFocus<HTMLDivElement>({ disabled, emitValidationState });
  const eventListener = { onChange, onInvalid };

  return (
    <Flex
      role="group"
      gap={4}
      flexDirection="column"
      data-testid={dataTestId}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      ref={wrapperRef}
      className={clsx(containerClassName, consumerClassName)}
      style={consumerStyle}
      {...containerFocusProps}
      {...dataAttributesProps}
    >
      <InputGroup
        style={{
          gridAutoColumns: `${'auto '.repeat(
            slots.prefix.buttons.length,
          )}1fr${' auto'.repeat(slots.suffix.buttons.length)}`,
        }}
        className={clsx(styles.wrapper, styles.variant[variant], {
          [styles.hasError]: showError,
          [styles.disabled]: disabledOrAriaDisabled,
          [styles.readOnly]: readOnly,
        })}
      >
        <BaseInputContext.Provider value={context}>
          {slots.prefix.buttons}
          <div
            className={clsx(
              styles.container,
              inputGroupClassName,
              focusClassName,
              {
                [styles.isAutoFilled]: isAutoFilled,
                [`${styles.containerPadding.none}`]:
                  hasInnerReservedSuffix && hasInnerPrefix,
                [`${styles.containerPadding.rightNone}`]:
                  hasInnerReservedSuffix && !hasInnerPrefix,
                [`${styles.containerPadding.leftUnset}`]: hasInnerPrefix,
                [`${styles.containerPadding.rightUnset}`]: hasInnerSuffix,
              },
            )}
            onClick={() => inputRef.current?.focus()} // clicking on the icons should focus the input
          >
            {hasInnerPrefix && (
              <BaseInputPrefix {...slots.prefix.props}>
                {slots.prefix.rest}
              </BaseInputPrefix>
            )}

            <input
              id={id}
              ref={inputRef}
              required={required}
              disabled={!!disabled}
              aria-disabled={ariaDisabled}
              onAnimationStart={onAnimationStart}
              readOnly={readOnly}
              className={styles.input}
              placeholder={placeholder}
              type={type}
              value={inputValue}
              pattern={pattern}
              max={max}
              min={min}
              step={step}
              maxLength={maxLength}
              minLength={minLength}
              {..._mergeProps(
                remainingInputProps,
                focusProps,
                controlState
                  ? { 'aria-label': ariaLabelProp, ...inputProps }
                  : ariaProps,
                eventListener,
              )}
            />
            {hasInnerSuffix && (
              <BaseInputSuffix {...slots.suffix.props}>
                {slots.suffix.rest}
              </BaseInputSuffix>
            )}
            {!readOnly &&
              (hasInnerReservedSuffix ? (
                <>
                  {hasInnerSuffix && (
                    <Divider
                      orientation="vertical"
                      className={styles.divider}
                      aria-hidden
                    />
                  )}
                  {slots.reservedSuffix.rest}
                </>
              ) : (
                <>
                  {hasInnerSuffix && showClearButton && (
                    <Divider
                      orientation="vertical"
                      className={styles.divider}
                      aria-hidden
                    />
                  )}
                  {showClearButton && <ClearButton onClear={onClear} />}
                </>
              ))}
          </div>
          {slots.suffix.buttons}
        </BaseInputContext.Provider>
      </InputGroup>
      {controlState && hint && (
        <BaseItem
          variant={showError ? 'error' : 'hint'}
          disabled={disabledOrAriaDisabled}
          {...hintProps}
        >
          {hint}
        </BaseItem>
      )}
    </Flex>
  );
});

/**
 * Base component used internally for our input components.
 * @internal
 */
export const BaseInput = Object.assign(BaseInputRoot, {
  Prefix: BaseInputPrefix,
  Suffix: BaseInputSuffix,
  ReservedSuffix: BaseInputReservedSuffix,
  Button: BaseInputButton,
});

(BaseInput as typeof BaseInput & { displayName: string }).displayName =
  'BaseInput';
